import { useEffect, useRef, useState } from 'react';
import { FolderOpen, PackageOpen, Trash2, Upload } from 'lucide-react';
import { useMultipartTransfer, type MultipartTransport } from '@ludolio/shared-games';
import {
  Button,
  Input,
  Progress,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@ludolio/shared-ui';
import { BuildStatusBadge, SectionTitle } from '@/components/common';
import { getErrorMessage, labsApi } from '@/lib/api';
import { formatBytes, formatDate } from '@/lib/format';
import type { LabProjectDetail, LabRelease, LabSourcePackage } from '@/lib/types';
import {
  prepareExistingZip,
  prepareSourceFolder,
  removeStaleSourcePackages,
  type PreparedSourcePackage,
} from '@/lib/source-packaging/package-writer';

function engineLabel(engine: LabSourcePackage['engine'], scanned = false): string {
  if (engine === 'unknown') return scanned ? 'Unknown engine' : 'Detecting engine';
  return engine.charAt(0).toUpperCase() + engine.slice(1);
}

export function SourcePackagePanel({
  release,
  reusableSourcePackages,
  editable,
  onChange,
}: {
  release: LabRelease;
  reusableSourcePackages: LabSourcePackage[];
  editable: boolean;
  onChange: (detail: LabProjectDetail) => void;
}) {
  const folderInputRef = useRef<HTMLInputElement>(null);
  const zipInputRef = useRef<HTMLInputElement>(null);
  const packagingController = useRef<AbortController | null>(null);
  const transfer = useMultipartTransfer();
  const [prepared, setPrepared] = useState<PreparedSourcePackage | null>(null);
  const preparedRef = useRef<PreparedSourcePackage | null>(null);
  const uploadingRef = useRef(false);
  preparedRef.current = prepared;
  const [packaging, setPackaging] = useState<{ completed: number; total: number; path: string } | null>(null);
  const [additionalExclusions, setAdditionalExclusions] = useState('');
  const [busy, setBusy] = useState(false);
  const current = release.sourcePackage;
  const reusable = reusableSourcePackages.filter((source) => source.id !== current?.id);

  useEffect(() => {
    void removeStaleSourcePackages();
    return () => {
      if (!uploadingRef.current) void preparedRef.current?.cleanup();
    };
  }, []);

  const replacePrepared = async (next: PreparedSourcePackage | null) => {
    await prepared?.cleanup();
    setPrepared(next);
  };

  const selectFolder = async (files: File[]) => {
    packagingController.current?.abort();
    const controller = new AbortController();
    packagingController.current = controller;
    setPackaging({ completed: 0, total: files.length, path: '' });
    try {
      const result = await prepareSourceFolder(
        files,
        additionalExclusions.split(','),
        (progress) => setPackaging({
          completed: progress.completedFiles,
          total: progress.totalFiles,
          path: progress.currentPath,
        }),
        controller.signal,
      );
      await replacePrepared(result);
    } catch (error) {
      if ((error as Error).name !== 'AbortError') toast.error(getErrorMessage(error));
    } finally {
      setPackaging(null);
      if (packagingController.current === controller) packagingController.current = null;
    }
  };

  const upload = async () => {
    if (!prepared) return;
    uploadingRef.current = true;
    const transport: MultipartTransport<string> = {
      initiate: async ({ filename, fileSize, totalParts, checksum }) => {
        const result = await labsApi.initiateSource({
          releaseId: release.id,
          filename,
          fileSize,
          totalParts,
          checksum,
          manifest: prepared.manifest,
        });
        return result.sourcePackageId;
      },
      getPartUrls: async (sourcePackageId, partNumbers) =>
        (await labsApi.getSourcePartUrls(sourcePackageId, partNumbers)).urls,
      complete: async (sourcePackageId, { parts }) => {
        onChange(await labsApi.completeSource(sourcePackageId, release.id, parts));
      },
      abort: async (sourcePackageId) => {
        await labsApi.abortSource(sourcePackageId);
      },
    };
    try {
      const uploaded = await transfer.upload(prepared.file, transport);
      if (uploaded) {
        toast.success('Source uploaded. The security check has started.');
        await replacePrepared(null);
        transfer.reset();
      }
    } finally {
      uploadingRef.current = false;
    }
  };

  const run = async (action: () => Promise<LabProjectDetail>) => {
    setBusy(true);
    try {
      onChange(await action());
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <SectionTitle>Source project</SectionTitle>
      {current ? (
        <div className="mb-5 grid gap-3 rounded-lg border p-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
          <div className="min-w-0">
            <p className="font-medium">{engineLabel(current.engine, current.status === 'passed' || current.status === 'failed' || current.status === 'error')} source</p>
            <p className="text-sm text-muted-foreground">
              {formatBytes(current.fileSize)}
              {current.fileCount > 0 ? ` · ${current.fileCount.toLocaleString()} files` : ''}
              {` · ${formatDate(current.createdAt)}`}
            </p>
            {current.failureReason && <p className="mt-1 text-sm text-destructive [overflow-wrap:anywhere]">{current.failureReason}</p>}
          </div>
          <div className="flex items-center justify-between gap-2 sm:justify-end">
            <BuildStatusBadge status={current.status} />
            {editable && (
              <Button
                variant="ghost"
                size="icon"
                aria-label="Remove source package from this release"
                disabled={busy}
                onClick={() => void run(() => labsApi.removeSource(release.id))}
              >
                <Trash2 />
              </Button>
            )}
          </div>
        </div>
      ) : (
        <p className="mb-4 text-sm text-muted-foreground">
          Preserve the editable Godot, Unity, or Unreal project with this version.
        </p>
      )}

      {editable && (
        <div className="space-y-4">
          <input
            ref={folderInputRef}
            type="file"
            multiple
            className="hidden"
            {...({ webkitdirectory: '' } as Record<string, string>)}
            onChange={(event) => {
              const files = [...(event.target.files ?? [])];
              event.target.value = '';
              if (files.length > 0) void selectFolder(files);
            }}
          />
          <input
            ref={zipInputRef}
            type="file"
            accept=".zip,application/zip"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (!file) return;
              try {
                void replacePrepared(prepareExistingZip(file));
              } catch (error) {
                toast.error(getErrorMessage(error));
              }
            }}
          />

          {!prepared && !packaging && (
            <div className="space-y-3">
              <Input
                aria-label="Additional folders to exclude"
                placeholder="Additional folders to exclude, separated by commas"
                value={additionalExclusions}
                onChange={(event) => setAdditionalExclusions(event.target.value)}
              />
              <div className="flex flex-wrap gap-3">
                <Button variant="outline" onClick={() => folderInputRef.current?.click()}>
                  <FolderOpen />
                  Choose project folder
                </Button>
                <Button variant="outline" onClick={() => zipInputRef.current?.click()}>
                  <PackageOpen />
                  Choose prepared ZIP
                </Button>
              </div>
            </div>
          )}

          {packaging && (
            <div className="space-y-2" aria-live="polite">
              <Progress value={packaging.completed} max={Math.max(packaging.total, 1)} />
              <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
                <span className="truncate">Packaging {packaging.path || 'project…'}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => packagingController.current?.abort()}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {prepared && (
            <div className="space-y-3 rounded-lg border p-4">
              <div className="grid gap-2 sm:grid-cols-3">
                <div>
                  <p className="text-xs text-muted-foreground">Engine</p>
                  <p className="font-medium">{engineLabel(prepared.manifest.engine)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Package</p>
                  <p className="font-medium">{formatBytes(prepared.file.size)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Files</p>
                  <p className="font-medium">
                    {prepared.includedFiles.length > 0
                      ? prepared.includedFiles.length.toLocaleString()
                      : 'Verified after upload'}
                  </p>
                </div>
              </div>
              {prepared.manifest.exclusions.length > 0 && (
                <p className="text-sm text-muted-foreground">
                  Excluded: {prepared.manifest.exclusions.join(', ')}
                </p>
              )}
              {prepared.includedFiles.length > 0 && (
                <div className="max-h-40 overflow-auto rounded-md bg-muted/50 p-3">
                  <ul className="space-y-1 text-xs text-muted-foreground">
                    {prepared.includedFiles.slice(0, 50).map((file) => (
                      <li key={file.path} className="flex justify-between gap-4">
                        <span className="truncate" title={file.path}>{file.path}</span>
                        <span className="shrink-0">{formatBytes(file.size)}</span>
                      </li>
                    ))}
                  </ul>
                  {prepared.includedFiles.length > 50 && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      And {(prepared.includedFiles.length - 50).toLocaleString()} more files
                    </p>
                  )}
                </div>
              )}
              {transfer.isUploading && <Progress value={transfer.progress.percentComplete} />}
              {transfer.progress.error && <p className="text-sm text-destructive">{transfer.progress.error}</p>}
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  disabled={transfer.isUploading}
                  onClick={() => void replacePrepared(null)}
                >
                  Discard
                </Button>
                {transfer.isUploading ? (
                  <Button variant="outline" onClick={() => void transfer.abort()}>Cancel upload</Button>
                ) : (
                  <Button onClick={() => void upload()}>
                    <Upload />
                    Upload source
                  </Button>
                )}
              </div>
            </div>
          )}

          {reusable.length > 0 && (
            <Select onValueChange={(id) => void run(() => labsApi.attachSource(release.id, id))}>
              <SelectTrigger className="w-full sm:w-96" aria-label="Use an earlier source package">
                <SelectValue placeholder="Use source from an earlier version" />
              </SelectTrigger>
              <SelectContent>
                {reusable.map((source) => (
                  <SelectItem key={source.id} value={source.id}>
                    {engineLabel(source.engine, true)} · {formatBytes(source.fileSize)} · {formatDate(source.createdAt)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}
    </section>
  );
}
